import type { PostProcessorModule, TOptions } from 'i18next'
import { PluralizationRulesMap } from './pluralization-rules'

export const pipePostProcessor: PostProcessorModule = {
  name: 'pipePostProcessor',
  type: 'postProcessor',
  process(value, _key, options, translator) {
    if (value.includes('|') && (options.count || options.count === 0)) {
      return processPluralization({ value, count: options.count, translator })
    }

    if (value.includes('|') && !options.count) {
      return processInterpolation({ value, options })
    }

    return value
  },
}

interface ProcessPluralizationOptions {
  value: string
  count: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  translator: any
}

const processPluralization = ({
  value,
  count,
  translator,
}: ProcessPluralizationOptions): string => {
  let index: number

  const SINGULAR_FORM = 0

  const PLURAL_FORM = 1

  const splitTranslation = value.split('|').map((str) => str.trim())

  const lang = translator.language || 'en'

  const rule = new Intl.PluralRules(lang)

  const formOptions = rule.resolvedOptions().pluralCategories

  const pluralizationRule = PluralizationRulesMap.get(lang)

  const isSimplePluralization = formOptions.length === 2

  if (isSimplePluralization || !pluralizationRule) {
    index = count === 1 ? SINGULAR_FORM : PLURAL_FORM
  } else {
    index = pluralizationRule(count, formOptions.length)
  }

  const chosenForm = splitTranslation[index] ?? splitTranslation[splitTranslation.length - 1]

  return chosenForm.replace(/{count}/g, String(count))
}

interface ProcessInterpolationOptions {
  value: string
  options: TOptions
}

const processInterpolation = ({ value, options }: ProcessInterpolationOptions): string => {
  const allOptionsWithoutLanguage = Object.keys(options).filter((option) => option !== 'lng')

  const splitTranslation = value.split('|').map((str) => str.trim())

  if (allOptionsWithoutLanguage.length === 0) {
    return splitTranslation.find((str) => !str.includes('{')) || splitTranslation[0]
  }

  const getMatchCount = (str: string): number =>
    allOptionsWithoutLanguage.reduce((count, key) => {
      const optionValue = String(options[key])
      return str.includes(optionValue) ? count + 1 : count
    }, 0)

  const chosenString = splitTranslation.reduce((best, current) => {
    const bestCount = getMatchCount(best)
    const currentCount = getMatchCount(current)

    return currentCount > bestCount ? current : best
  })

  return chosenString
}
